import { useState, useEffect, useCallback, useMemo } from 'react';
import { TextInput } from '@components/shared/form/TextInput';
import CustomSelect from '@components/shared/form/CustomSelectInput';
import { useTexts } from 'utils/useTexts';
import { Button } from '@components/shared/Button/Button';
import { useFormContext } from '@context/FormContext';
import { getMake, getModel, getYear } from '@services/enterLicensePlateManually';
import { formatOptions, IOption } from '@services/utils';
import { getMileages } from '@shared/constants/mileages';
import useForm, { validate } from '@shared/hooks/useForm';
import { CDX_ACTIONS, IManualLicensePlateInfoForm, IVerifyYourIdentity } from 'types/form';
import { PhoneTextInput } from '@components/shared/form/PhoneTextInput';
import { capitalize, convertArrayToSnakeCase } from 'utils/stringFormat';
import { useProve } from '@context/proveProvider';
import ProveClientSdk from '@services/proveClientSdk';
import { Flowtype, IProveStartPayload } from 'types/proveIdentity';
import { start } from '@services/prove';
import useEffectOnce from '@shared/hooks/useEffectOnce';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import { SEGMENT_SCREEN_PARAMS } from '@shared/constants/segmentConstants';
import { useScreenNavigator } from '@shared/hooks/useScreenNavigator';
import { PriceInput } from '@components/shared/form/PriceTextInput';

type ManualLicensePlateForm = Omit<IManualLicensePlateInfoForm, 'makeName'> & IVerifyYourIdentity;

const getOptionValue = (search: string, options: IOption[]): string => {
  // First try to find by label
  const optionByLabel = options.find((opt) => opt.label.toLowerCase() === search.toLowerCase());
  if (optionByLabel) return optionByLabel.value;

  // If not found try to find by value
  const optionByValue = options.find((opt) => opt.value.toLowerCase() === search.toLowerCase());
  if (optionByValue) return optionByValue.value;

  return '';
};

const EnterLicensePlateManuallyForm = () => {
  const { t } = useTexts();
  const mileages = getMileages();
  const [years, setYears] = useState<IOption[]>([]);
  const [makes, setMakes] = useState<IOption[]>([]);
  const [models, setModels] = useState<IOption[]>([]);
  const [isSelectChanged, setIsSelectChanged] = useState(false);
  const {
    currentStep,
    updateManualLicensePlateInfoFormData,
    updateVerifyYourIdentity,
    manualLicensePlateInfoForm,
    verifyYourIdentity,
    isLoading,
    widgetInitVariables,
    submitStart,
    submitSuccess,
    dispatch,
  } = useFormContext();
  const { next } = useScreenNavigator();
  const { setAuthToken, setCorrelationId, setNextStep } = useProve();
  const { trackFormSubmit, trackPage, startInteraction } = useAnalytics(SEGMENT_SCREEN_PARAMS.VEHICLE_SELECTION);
  const parsedUTM = JSON.parse(
    widgetInitVariables?.tracking?.extraData || '{}'
  );

  const [isMobileFlow, setIsMobileFlow] = useState(false);

  const desktopValidationSchema = {
    year: validate.string('year').required(t('enterLicensePlateManually.validation.year')),
    make: validate.string('make').required(t('enterLicensePlateManually.validation.make')),
    model: validate.string('model').required(t('enterLicensePlateManually.validation.model')),
    estimatedMileage: validate.string('estimatedMileage').required(t('enterLicensePlateManually.validation.estimatedMileage')),
    dateOfBirth: validate.string('dateOfBirth').required(t('enterLicensePlateManually.validation.dateOfBirth')).isAdult(),
    phone: validate
      .string('phone')
      .required(t('enterLicensePlateManually.validation.phone.required'))
      .matches(/^\d{10}$/, t('enterLicensePlateManually.validation.phone.validPhoneNumber')),
    currentMonthlyPayment: validate
      .number('currentMonthlyPayment')
      .minNumber(1, t('enterLicensePlateManually.validation.currentMonthlyPayment.required'))
      .isNumber(t('enterLicensePlateManually.validation.currentMonthlyPayment.isNumber')),
  };

  const mobileValidationSchema = {
    year: validate.string('year').required(t('enterLicensePlateManually.validation.year')),
    make: validate.string('make').required(t('enterLicensePlateManually.validation.make')),
    model: validate.string('model').required(t('enterLicensePlateManually.validation.model')),
    estimatedMileage: validate.string('estimatedMileage').required(t('enterLicensePlateManually.validation.estimatedMileage')),
    currentMonthlyPayment: validate
      .number('currentMonthlyPayment')
      .minNumber(1, t('enterLicensePlateManually.validation.currentMonthlyPayment.required'))
      .isNumber(t('enterLicensePlateManually.validation.currentMonthlyPayment.isNumber')),
    dateOfBirth: validate.string('dateOfBirth').required(t('enterLicensePlateManually.validation.dateOfBirth')).isAdult(),
    phone: validate.string('phone').matches(/^\d{10}$/, t('enterLicensePlateManually.validation.phone.validPhoneNumber')),
  };

  const validationSchema = isMobileFlow ? mobileValidationSchema : desktopValidationSchema;

  const { errors, handleChange, handleSubmit } = useForm<ManualLicensePlateForm>({
    initialValues: {
      ...manualLicensePlateInfoForm,
      ...verifyYourIdentity,
    },
    validationSchema,
    onSubmit: async () => {
      const currentYear = new Date().getFullYear();
      const formSubmitManualPlateValues = { ...manualLicensePlateInfoForm, ...verifyYourIdentity };
      if (
        Number(manualLicensePlateInfoForm.year) < currentYear - 10 ||
        manualLicensePlateInfoForm.estimatedMileage === 'Over 150,000 miles'
      ) {
        next(currentStep, 'preSubmitOutcome', {
          manualLicenseForm: manualLicensePlateInfoForm,
        }, false);
        if (manualLicensePlateInfoForm.estimatedMileage === 'Over 150,000 miles') {
          trackFormSubmit('Vehicle Information Provided', {
            submission_method: 'manual',
            form_context: 'vehicle_lookup',
            qualified_status: false,
            qualified_fail_reason: 'estimated_mileage',
            ...formSubmitManualPlateValues,
          });
        }
        if (Number(manualLicensePlateInfoForm.year) < currentYear - 10) {
          trackFormSubmit('Vehicle Information Provided', {
            submission_method: 'manual',
            form_context: 'vehicle_lookup',
            qualified_status: false,
            qualified_fail_reason: 'year',
            ...formSubmitManualPlateValues,
          });
        }
      } else {
        let requestPayload = {} as IProveStartPayload;
        if (isMobileFlow) {
          requestPayload = {
            flowType: Flowtype.MOBILE,
            dob: verifyYourIdentity.dateOfBirth,
          };
        } else {
          requestPayload = {
            flowType: Flowtype.DESKTOP,
            phoneNumber: `+1${verifyYourIdentity.phone}`,
            dob: verifyYourIdentity.dateOfBirth,
          };
        }
        submitStart();
        const startResponse = await start(requestPayload, parsedUTM?.anonymous_id);

        if ('error' in startResponse) {
          dispatch({
            type: CDX_ACTIONS.SUBMIT_API_ERROR,
            payload: startResponse,
          });
          trackFormSubmit('Form Submitted', {
            submission_status: false,
            submission_method: 'manual',
            submission_fail_reason: 'lookup_timeout',
          });
        } else {
          submitSuccess();
          trackFormSubmit('Form Submitted', {
            submission_status: true,
            submission_method: 'manual',
            forward_screen_name: 'prove_identity',
          });

          trackFormSubmit('Vehicle Information Provided', {
            submission_method: 'manual',
            form_context: 'vehicle_lookup',
            qualified_status: true,
            ...formSubmitManualPlateValues,
          });
          setAuthToken(startResponse?.authToken);
          setCorrelationId(startResponse?.correlationId);
          setNextStep(startResponse?.next);
          next(currentStep, 'postSubmitOutcome', {
            isMobile: isMobileFlow,
          }, false);
        }
      }
    },
    onValidationError: (errors) => {
      trackFormSubmit('Form Submitted', {
        submission_status: false,
        submission_method: 'manual',
        submission_fail_reason: 'validation_error',
        validation_errors: convertArrayToSnakeCase(Object.keys(errors)),
      });
    },
  });

  useEffect(() => {
    const proveAuthManager = new ProveClientSdk();
    const isMobile = proveAuthManager.authCheck();
    setIsMobileFlow(isMobile);
  }, []);

  const getYears = useCallback(async () => {
    const yearsResponse = await getYear();
    if ('error' in yearsResponse) {
      dispatch({
        type: CDX_ACTIONS.GET_API_ERROR,
        payload: yearsResponse,
      });
    } else {
      const sortedYears = yearsResponse.sort((a, b) => Number(b.modelYear) - Number(a.modelYear));
      setYears(formatOptions(sortedYears, 'modelYear', 'modelYear'));
    }
  }, []);

  const getMakes = useCallback(async (year: string) => {
    const makeResponse = await getMake(year);
    if ('error' in makeResponse) {
      dispatch({
        type: CDX_ACTIONS.GET_API_ERROR,
        payload: makeResponse,
      });
    } else {
      setMakes(formatOptions(makeResponse, 'makeId', 'makeName'));
    }
  }, []);

  const getModels = useCallback(async (year: string, make: string) => {
    const modelResponse = await getModel(year, make);
    if ('error' in modelResponse) {
      dispatch({
        type: CDX_ACTIONS.GET_API_ERROR,
        payload: modelResponse,
      });
    } else {
      setModels(formatOptions(modelResponse, 'modelName', 'modelName'));
    }
  }, []);

  useEffect(() => {
    if (!isSelectChanged) {
      getYears();
    }
  }, [isSelectChanged, getYears]);

  useEffect(() => {
    if (manualLicensePlateInfoForm?.year.length > 0 && !isSelectChanged) {
      getMakes(manualLicensePlateInfoForm?.year);
    }
  }, [manualLicensePlateInfoForm?.year, isSelectChanged, getMakes]);

  useEffect(() => {
    if (
      manualLicensePlateInfoForm?.year.length > 0 &&
      manualLicensePlateInfoForm?.make.length > 0 &&
      makes.length > 0 &&
      !isSelectChanged
    ) {
      getModels(manualLicensePlateInfoForm?.year, getOptionValue(capitalize(manualLicensePlateInfoForm?.make), makes));
    }
  }, [manualLicensePlateInfoForm?.make, manualLicensePlateInfoForm?.year, makes, isSelectChanged, getModels]);

  useMemo(() => {
    const optionValueModel = getOptionValue(capitalize(manualLicensePlateInfoForm.model), models);
    handleChange('model', optionValueModel);
    updateManualLicensePlateInfoFormData({ model: optionValueModel });
  }, [manualLicensePlateInfoForm.model, models]);

  const handleInputChange = async <K extends keyof ManualLicensePlateForm>(field: K, value: ManualLicensePlateForm[K]) => {
    handleChange(field, value);

    if (field === 'dateOfBirth' || field === 'phone') {
      updateVerifyYourIdentity({ [field]: value });
    } else {
      updateManualLicensePlateInfoFormData({ [field]: value });
    }
    if (field === 'year') {
      setIsSelectChanged(true);
      updateManualLicensePlateInfoFormData({ make: '', model: '' });
      setMakes([]);
      setModels([]);
      await getMakes(value.toString());
    } else if (field === 'make') {
      setIsSelectChanged(true);
      setModels([]);
      const option = makes.find((opt) => opt.value === value);
      updateManualLicensePlateInfoFormData({ model: '', makeName: option?.label });
      await getModels(manualLicensePlateInfoForm.year, value.toString());
    }
  };

  useEffectOnce(() => trackPage(t('pageTitles.EnterLicensePlateManually')));

  return (
    <div>
      <form
        onSubmit={handleSubmit}
        onFocus={startInteraction}
      >
        <h1 className="iLendingCDX-form-title">{t('enterLicensePlateManually.title')}</h1>
        <p className={'iLendingCDX-mb-4'}> {t('enterLicensePlateManually.simplyEnterDetails')}</p>
        <div>
          <CustomSelect
            name="year"
            label={t('enterLicensePlateManually.form.year')}
            placeholder={t('enterLicensePlateManually.form.year')}
            className={'iLendingCDX-mb-4'}
            options={years}
            value={getOptionValue(manualLicensePlateInfoForm.year, years)}
            errorMessage={errors.year}
            disabled={years.length === 0}
            onChange={(e) => handleInputChange('year', e.target.value)}
          />

          <CustomSelect
            name="make"
            label={t('enterLicensePlateManually.form.make')}
            placeholder={t('enterLicensePlateManually.form.year')}
            className={'iLendingCDX-mb-6'}
            options={makes}
            value={getOptionValue(capitalize(manualLicensePlateInfoForm?.make), makes)}
            errorMessage={errors.make}
            disabled={!manualLicensePlateInfoForm.year || makes.length === 0}
            onChange={(e) => handleInputChange('make', e.target.value)}
          />
          <CustomSelect
            name="model"
            label={t('enterLicensePlateManually.form.model')}
            className={'iLendingCDX-mb-6'}
            options={models}
            value={getOptionValue(manualLicensePlateInfoForm?.model, models)}
            placeholder={t('enterLicensePlateManually.form.model')}
            errorMessage={errors.model}
            disabled={!manualLicensePlateInfoForm.year || !manualLicensePlateInfoForm.make || models.length === 0}
            onChange={(e) => handleInputChange('model', e.target.value)}
          />
          <CustomSelect
            name="estimatedMileage"
            label={t('enterLicensePlateManually.form.selectEstimatedMileage')}
            className={'iLendingCDX-mb-6'}
            options={mileages}
            placeholder={t('enterLicensePlateManually.form.selectEstimatedMileage')}
            value={manualLicensePlateInfoForm.estimatedMileage}
            errorMessage={errors.estimatedMileage}
            onChange={(e) => handleInputChange('estimatedMileage', e.target.value)}
          />

          <PriceInput
            label={t('enterLicensePlateManually.form.currentMonthlyPayment')}
            name="currentMonthlyPayment"
            className={'iLendingCDX-mb-6'}
            errorMessage={errors.currentMonthlyPayment}
            value={manualLicensePlateInfoForm.currentMonthlyPayment}
            onChange={(value) =>
              handleInputChange('currentMonthlyPayment', Number(value) as IManualLicensePlateInfoForm['currentMonthlyPayment'])
            }
          />

          <h1 className="iLendingCDX-form-title">{t('verifyVehicleInfo.title2')}</h1>
          <p className={'iLendingCDX-mb-8'}>{t('verifyVehicleInfo.weCanPrefill')}</p>

          <TextInput
            label={t('verifyVehicleInfo.form.dateOfBirth')}
            className={'iLendingCDX-mb-4'}
            name="dateOfBirth"
            value={verifyYourIdentity.dateOfBirth}
            errorMessage={errors.dateOfBirth}
            type="date"
            max="9999-12-31"
            onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
          />

          {!isMobileFlow && (
            <PhoneTextInput
              label={t('verifyVehicleInfo.form.phone')}
              className={'iLendingCDX-mb-6'}
              name="phone"
              value={verifyYourIdentity.phone}
              errorMessage={errors.phone}
              onChange={(value) => handleInputChange('phone', value as IVerifyYourIdentity['phone'])}
            />
          )}

          {isMobileFlow ? (
            <p className="iLendingCDX-para iLendingCDX-mb-4">{t('verifyVehicleInfo.termsAndConditionsText')}</p>
          ) : (
            <p className="iLendingCDX-para iLendingCDX-mb-4">{t('verifyVehicleInfo.termsAndConditionsText1')}</p>
          )}

          <p className="iLendingCDX-para iLendingCDX-mb-4 iLendingCDX-input-label--check">
            {t('verifyVehicleInfo.byClickingContinue')}
            <a
              href="https://ilendingcarloanrefinancing.com/privacy-policy/"
              target="_blank"
              rel="noreferrer"
            >
              {t('verifyVehicleInfo.termsAndConditionsText2')}
              {'.'}
            </a>
          </p>


          <Button
            type="submit"
            className="iLendingCDX-button--rounded"
            size="lg"
            isLoading={isLoading}
          >
            {t('enterLicensePlateManually.form.continue')}
          </Button>
        </div>
      </form>
    </div>
  );
};
export default EnterLicensePlateManuallyForm;
